import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useTransition,
} from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { FixedSizeList as VirtualList } from "react-window";
import axios from "../../utils/axiosInstance";
import { TextSlicer } from "../../utils/textSlicerDots";
export default function AssignUsers({ open, onClose, appId }) {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [stagedUsers, setStagedUsers] = useState([]);
  const [stagedForRemoval, setStagedForRemoval] = useState([]);
  const [availableSearchQuery, setAvailableSearchQuery] = useState("");
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [application, setApplication] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [isPending, startTransition] = useTransition();

  const showApplication = useCallback((message, severity = "success") => {
    setApplication({ open: true, message, severity });
  }, []);

  const handleCloseApplication = useCallback(() => {
    setApplication((prev) => ({ ...prev, open: false }));
  }, []);

  // Fetch users when dialog opens
  useEffect(() => {
    if (!open || !appId) return;

    const loadUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [assignedRes, allRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_GATEAWAY}/users`, {
            params: { companyId: process.env.REACT_APP_COMPANY_ID, appId },
            headers,
          }),
          axios.get(`${process.env.REACT_APP_API_GATEAWAY}/users`, {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
            headers,
          }),
        ]);

        const assigned = Array.isArray(assignedRes.data)
          ? assignedRes.data
          : [];
        const allUsers = Array.isArray(allRes.data) ? allRes.data : [];

        const assignedIds = new Set(assigned.map((u) => u?.id).filter(Boolean));
        const available = allUsers.filter(
          (u) => u?.id && !assignedIds.has(u.id)
        );

        setAssignedUsers(assigned);
        setAvailableUsers(available);
        setStagedUsers([]);
        setStagedForRemoval([]);
        setAvailableSearchQuery("");
        setSelectedSearchQuery("");
      } catch (error) {
        console.error("Error fetching users:", error);
        showApplication("Error fetching users", "error");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [open, appId, showApplication]);

  // Memoized filtered users
  const filteredAvailableUsers = useMemo(() => {
    if (!availableSearchQuery) return availableUsers;
    const q = availableSearchQuery.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user?.name?.toLowerCase().includes(q) ||
        user?.email?.toLowerCase().includes(q) ||
        user?.phone?.toLowerCase().includes(q)
    );
  }, [availableUsers, availableSearchQuery]);

  const allSelectedRecipients = useMemo(() => {
    const existingRecipients = assignedUsers.filter(
      (user) => !stagedForRemoval.some((staged) => staged.id === user.id)
    );
    return [...stagedUsers, ...existingRecipients];
  }, [assignedUsers, stagedUsers, stagedForRemoval]);

  const filteredSelectedRecipients = useMemo(() => {
    if (!selectedSearchQuery) return allSelectedRecipients;
    const q = selectedSearchQuery.toLowerCase();
    return allSelectedRecipients.filter(
      (user) =>
        user?.name?.toLowerCase().includes(q) ||
        user?.email?.toLowerCase().includes(q) ||
        user?.phone?.toLowerCase().includes(q)
    );
  }, [allSelectedRecipients, selectedSearchQuery]);

  // Optimized add user handler with transition
  const handleAddFromStaged = useCallback(
    (user) => {
      if (!user?.id) return;

      startTransition(() => {
        const isStagedForRemoval = stagedForRemoval.some(
          (staged) => staged.id === user.id
        );

        if (isStagedForRemoval) {
          setStagedForRemoval((prev) =>
            prev.filter((staged) => staged.id !== user.id)
          );
          setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
          showApplication(`${user.name} restored to recipients`, "success");
        } else {
          setAvailableUsers((prev) => prev.filter((u) => u.id !== user.id));
          setStagedUsers((prev) => [user, ...prev]);
        }
      });
    },
    [stagedForRemoval, showApplication]
  );

  // Optimized remove user handler with transition
  const handleRemoveRecipient = useCallback(
    (user) => {
      if (!user?.id) return;

      startTransition(() => {
        const isStagedUser = stagedUsers.some(
          (stagedUser) => stagedUser.id === user.id
        );

        if (isStagedUser) {
          setStagedUsers((prev) => prev.filter((u) => u.id !== user.id));
          setAvailableUsers((prev) => [user, ...prev]);
          showApplication(`${user.name} removed from selection`, "success");
        } else {
          const userToRemove = assignedUsers.find((u) => u.id === user.id);
          if (!userToRemove) return;

          setStagedForRemoval((prev) => [userToRemove, ...prev]);
          setAvailableUsers((prev) => [userToRemove, ...prev]);
          showApplication(`${userToRemove.name} staged for removal`, "success");
        }
      });
    },
    [stagedUsers, assignedUsers, showApplication]
  );

  // Save changes handler
  const handleSaveChanges = useCallback(async () => {
    if (stagedUsers.length === 0 && stagedForRemoval.length === 0) {
      showApplication("No changes to save", "warning");
      return;
    }

    setSavingRecipients(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      if (stagedUsers.length > 0) {
        await axios.put(
          `${process.env.REACT_APP_API_GATEAWAY}/users/updateUsersAppId`,
          { userIds: stagedUsers.map((u) => u.id), appId },
          { headers }
        );
      }

      if (stagedForRemoval.length > 0) {
        await axios.put(
          `${process.env.REACT_APP_API_GATEAWAY}/users/updateUsersAppId`,
          { userIds: stagedForRemoval.map((u) => u.id), appId: null },

          { headers }
        );
      }

      const addedCount = stagedUsers.length;
      const removedCount = stagedForRemoval.length;
      let message = "";

      if (addedCount > 0 && removedCount > 0) {
        message = `${addedCount} user(s) added and ${removedCount} user(s) removed successfully`;
      } else if (addedCount > 0) {
        message = `${addedCount} user(s) added successfully`;
      } else if (removedCount > 0) {
        message = `${removedCount} user(s) removed successfully`;
      }

      showApplication(message, "success");
      setStagedUsers([]);
      setStagedForRemoval([]);
      onClose();
    } catch (error) {
      console.error("Error saving changes:", error);
      showApplication("Error saving changes", "error");
    } finally {
      setSavingRecipients(false);
    }
  }, [stagedUsers, stagedForRemoval, appId, showApplication, onClose]);

  const handleClose = useCallback(() => {
    setStagedUsers([]);
    setStagedForRemoval([]);
    onClose();
  }, [onClose]);

  // Memoized UserListItem
  const UserListItem = useCallback(
    ({ user, showAddButton, showRemoveButton }) => {
      if (!user?.id) return null;

      const isStagedUser = stagedUsers.some((s) => s.id === user.id);
      const isStagedForRemoval = stagedForRemoval.some((s) => s.id === user.id);
      const isExistingRecipient =
        assignedUsers.some((u) => u.id === user.id) && !isStagedForRemoval;

      return (
        <ListItem
          sx={{
            height: 75,
            border: 1,
            borderColor: isStagedUser
              ? "success.main"
              : isStagedForRemoval
              ? "error.main"
              : "divider",
            borderRadius: 1,

            backgroundColor: isStagedUser
              ? "success.light"
              : isStagedForRemoval
              ? "error.light"
              : "background.paper",
            opacity: isStagedForRemoval ? 0.6 : 1,
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textDecoration: isStagedForRemoval
                      ? "line-through"
                      : "none",
                  }}
                >
                  {user.name || "Unnamed"}
                </Typography>
                {isStagedUser && (
                  <Typography
                    variant="caption"
                    color="white"
                    fontWeight="bold"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 10,
                      fontSize: { xs: "8px", sm: "12px", md: "14px" },
                    }}
                  >
                    (TO ADD)
                  </Typography>
                )}
                {isStagedForRemoval && (
                  <Typography
                    variant="caption"
                    color="white"
                    fontWeight="bold"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 10,
                      fontSize: { xs: "8px", sm: "12px", md: "14px" },
                    }}
                  >
                    (TO REMOVE)
                  </Typography>
                )}
                {isExistingRecipient && (
                  <Typography
                    variant="caption"
                    color="primary.dark"
                    fontWeight="bold"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 10,
                      fontSize: { xs: "8px", sm: "12px", md: "14px" },
                    }}
                  >
                    (CURRENT)
                  </Typography>
                )}
              </Box>
            }
            secondary={
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "9px", sm: "14px", md: "15px" },
                    textDecoration: isStagedForRemoval
                      ? "line-through"
                      : "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {TextSlicer(user.email, 12)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textDecoration: isStagedForRemoval
                      ? "line-through"
                      : "none",
                  }}
                >
                  {user.phone || ""}
                </Typography>
              </Box>
            }
          />
          <ListItemSecondaryAction>
            {showAddButton && (
              <IconButton
                onClick={() => handleAddFromStaged(user)}
                sx={{
                  // XS: absolute top-right
                  position: { xs: "absolute", sm: "static", md: "static" },
                  top: { xs: 2, sm: "auto", md: "auto" },
                  right: { xs: -10, sm: "auto", md: "auto" },
                  // XS smaller size
                  width: { xs: 28, sm: "auto", md: "auto" },
                  height: { xs: 28, sm: "auto", md: "auto" },
                  p: { xs: 0.5, sm: 1, md: 1 },

                  backgroundColor: isStagedForRemoval
                    ? "primary.main"
                    : "success.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: isStagedForRemoval
                      ? "primary.dark"
                      : "success.dark",
                  },
                }}
              >
                <AddIcon sx={{ fontSize: { xs: 16, sm: 24, md: 24 } }} />
              </IconButton>
            )}
            {showRemoveButton && (
              <IconButton
                onClick={() => handleRemoveRecipient(user)}
                sx={{
                  position: { xs: "absolute", sm: "static", md: "static" },
                  top: { xs: 2, sm: "auto", md: "auto" },
                  right: { xs: -9, sm: "auto", md: "auto" },

                  // XS smaller size
                  width: { xs: 28, sm: "auto", md: "auto" },
                  height: { xs: 28, sm: "auto", md: "auto" },
                  p: { xs: 0.5, sm: 1, md: 1 },
                  backgroundColor: "error.main",
                  color: "white",
                  "&:hover": { backgroundColor: "error.dark" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </ListItemSecondaryAction>
        </ListItem>
      );
    },
    [
      stagedUsers,
      stagedForRemoval,
      assignedUsers,
      handleAddFromStaged,
      handleRemoveRecipient,
    ]
  );

  // Virtual list row renderer
  const VirtualRow = useCallback(
    ({ index, style, data }) => {
      const user = data.users[index];
      if (!user) return null;

      return (
        <div key={user.id} style={style}>
          <UserListItem
            user={user}
            showAddButton={data.showAddButton}
            showRemoveButton={data.showRemoveButton}
          />
        </div>
      );
    },
    [UserListItem]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "80vh", maxHeight: "800px" } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Manage Users{" "}
              {(stagedUsers.length > 0 || stagedForRemoval.length > 0) &&
                `(${stagedUsers.length} to add, ${stagedForRemoval.length} to remove)`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <CircularProgress color="#60a5fa" size={60} />
            </Box>
          ) : (
            <>
              {/* <Typography variant="body2" color="error" sx={{ mb: 2, fontStyle: "italic" }}>
                Selected Recipients will receive applications from following units
              </Typography> */}

              {(stagedUsers.length > 0 || stagedForRemoval.length > 0) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {stagedUsers.length > 0 &&
                    `${stagedUsers.length} user(s) to be added`}
                  {stagedUsers.length > 0 &&
                    stagedForRemoval.length > 0 &&
                    ", "}
                  {stagedForRemoval.length > 0 &&
                    `${stagedForRemoval.length} user(s) to be removed`}
                  . Click "Save Changes" to apply.
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  height: "95%",
                  gap: { xs: 0.5, sm: 2, md: 2 },
                }}
              >
                {/* Available Users Panel */}
                <UserPanel
                  title="Available Users"
                  users={filteredAvailableUsers}
                  searchQuery={availableSearchQuery}
                  setSearchQuery={setAvailableSearchQuery}
                  VirtualRow={VirtualRow}
                  showAddButton={true}
                  showRemoveButton={false}
                />

                {/* Selected Users Panel */}
                <UserPanel
                  title="Assigned Users"
                  users={filteredSelectedRecipients}
                  searchQuery={selectedSearchQuery}
                  setSearchQuery={setSelectedSearchQuery}
                  VirtualRow={VirtualRow}
                  showAddButton={false}
                  showRemoveButton={true}
                />
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: 0, // ✅ remove rounding
              bgcolor: "grey.400",
              color: "white",
              "&:hover": { bgcolor: "grey.500" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={
              (stagedUsers.length === 0 && stagedForRemoval.length === 0) ||
              savingRecipients
            }
            sx={{
               borderRadius: 0,
              bgcolor: "grey.600",
              "&:hover": { bgcolor: "grey.700" },
              "&:disabled": { bgcolor: "grey.300" },
            }}
          >
            {savingRecipients ? (
              <>
                <CircularProgress size={20} color="#60a5fa" sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              `Save Changes ${
                stagedUsers.length > 0 || stagedForRemoval.length > 0
                  ? `(+${stagedUsers.length}, -${stagedForRemoval.length})`
                  : ""
              }`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={application.open}
        autoHideDuration={4000}
        onClose={handleCloseApplication}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseApplication}
          severity={application.severity}
          sx={{ width: "100%" }}
        >
          {application.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// Separate UserPanel component
const UserPanel = React.memo(
  ({
    title,
    users,
    searchQuery,
    setSearchQuery,
    VirtualRow,
    showAddButton,
    showRemoveButton,
  }) => {
    const itemData = useMemo(
      () => ({
        users,
        showAddButton,
        showRemoveButton,
      }),
      [users, showAddButton, showRemoveButton]
    );

    return (
      <Box sx={{ width: "50%", height: "100%" }}>
        <Paper
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "grey.800",
          }}
        >
          <Box sx={{ p: 2, bgcolor: "grey.700", color: "white" }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
          </Box>

          <Box sx={{ p: 1, pb: 1 }}>
            <TextField
              placeholder="SEARCH"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "black" } }}
            />
          </Box>

          <Box sx={{ flex: 1, px: { xs: 1, sm: 2, md: 2 }, pb: 1 }}>
            {users.length > 0 ? (
              <VirtualList
                height={377}
                itemCount={users.length}
                itemSize={82}
                width="100%"
                itemData={itemData}
              >
                {VirtualRow}
              </VirtualList>
            ) : (
              <Typography
                variant="body2"
                color="grey.400"
                textAlign="center"
                sx={{ py: 4 }}
              >
                No users found
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }
);
